/**
 * InvestigationTeamModal — Workspace-level team management
 *
 * Features:
 *  - Shows team lead (non-removable) with prominent badge
 *  - Lists current team members with role selectors
 *  - Search & add members from the platform user pool
 *  - Remove members (except the lead)
 *  - Role options: member, expert, observer
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../../Components/CloseButton/CloseButton";
import {
  fetchTeamMembers,
  addTeamMember,
  removeTeamMember,
  TeamMemberResponse,
} from "../../api/workspaces";
import {
  fetchAllSecurityUsers,
  SecurityUserResponse,
} from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./InvestigationTeamModal.module.css";

const TEAM_ROLES = ["lead", "member", "expert", "observer"] as const;
type TeamRole = typeof TEAM_ROLES[number];

interface Props {
  workspaceGuid: string;
  teamLeadGuid: string;
  onClose: () => void;
  /** Called after any team mutation so the parent can refresh counts */
  onTeamChanged?: () => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

const InvestigationTeamModal: React.FC<Props> = ({
  workspaceGuid,
  teamLeadGuid,
  onClose,
  onTeamChanged,
}) => {
  const { t } = useAppTranslation("pages");

  // ── State ──
  const [members, setMembers] = useState<TeamMemberResponse[]>([]);
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [adding, setAdding] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // ── Load data ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamData, usersData] = await Promise.all([
        fetchTeamMembers(workspaceGuid),
        fetchAllSecurityUsers(),
      ]);
      // Filter out the team lead — they're shown in a separate banner
      setMembers(
        teamData.filter(
          (m) => m.userGuid.toUpperCase() !== teamLeadGuid.toUpperCase()
        )
      );
      setAllUsers(usersData.filter((u) => u.isActive));
    } catch {
      // Silently handle — modal will show empty state
    } finally {
      setLoading(false);
    }
  }, [workspaceGuid, teamLeadGuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!searchFocused) return;
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchFocused]);

  // ── User lookup map ──
  const userMap = useMemo(() => {
    const map = new Map<string, SecurityUserResponse>();
    allUsers.forEach((u) => map.set(u.userGuid.toUpperCase(), u));
    return map;
  }, [allUsers]);

  const getUser = useCallback(
    (guid: string): SecurityUserResponse | undefined =>
      userMap.get(guid.toUpperCase()),
    [userMap]
  );

  // ── Team lead user ──
  const leadUser = useMemo(() => getUser(teamLeadGuid), [getUser, teamLeadGuid]);

  // ── Current member guids (for filtering search) ──
  const memberGuids = useMemo(() => {
    const set = new Set<string>();
    set.add(teamLeadGuid.toUpperCase());
    members.forEach((m) => set.add(m.userGuid.toUpperCase()));
    return set;
  }, [members, teamLeadGuid]);

  // ── Search results ──
  const searchResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) return [];
    return allUsers
      .filter((u) => !memberGuids.has(u.userGuid.toUpperCase()))
      .filter(
        (u) =>
          u.displayName?.toLowerCase().includes(term) ||
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.screenName?.toLowerCase().includes(term)
      )
      .slice(0, 8);
  }, [searchTerm, allUsers, memberGuids]);

  // ── Add member ──
  const handleAdd = useCallback(
    async (userGuid: string) => {
      setAdding(true);
      try {
        await addTeamMember(workspaceGuid, { userGuid, role: "member" });
        setSearchTerm("");
        setSearchFocused(false);
        const updated = await fetchTeamMembers(workspaceGuid);
        setMembers(updated);
        onTeamChanged?.();
      } catch {
        // error handling
      } finally {
        setAdding(false);
      }
    },
    [workspaceGuid, onTeamChanged]
  );

  // ── Remove member ──
  const handleRemove = useCallback(
    async (userGuid: string) => {
      try {
        await removeTeamMember(workspaceGuid, userGuid);
        setMembers((prev) =>
          prev.filter(
            (m) => m.userGuid.toUpperCase() !== userGuid.toUpperCase()
          )
        );
        onTeamChanged?.();
      } catch {
        // error handling
      }
    },
    [workspaceGuid, onTeamChanged]
  );

  // ── Role change (optimistic update — no backend endpoint yet) ──
  const handleRoleChange = useCallback(
    (userGuid: string, newRole: string) => {
      setMembers((prev) =>
        prev.map((m) =>
          m.userGuid.toUpperCase() === userGuid.toUpperCase()
            ? { ...m, role: newRole }
            : m
        )
      );
      // TODO: call updateTeamMemberRole when backend supports it
    },
    []
  );

  // ── Render helpers ──
  const renderAvatar = (user: SecurityUserResponse | undefined) => {
    if (!user) {
      return (
        <div className={styles.avatar}>
          <span className={styles.avatarInitials}>??</span>
        </div>
      );
    }
    const img = profileImgUrl(user);
    return (
      <div className={styles.avatar}>
        {img ? (
          <img src={img} alt="" className={styles.avatarImg} />
        ) : (
          <span className={styles.avatarInitials}>
            {getInitials(user.displayName || user.name)}
          </span>
        )}
      </div>
    );
  };

  const renderUserInfo = (user: SecurityUserResponse | undefined, guid?: string) => {
    if (!user) {
      return (
        <div className={styles.userInfo}>
          <div className={styles.userName}>{guid ?? t("workspace.team.unknownUser")}</div>
        </div>
      );
    }
    return (
      <div className={styles.userInfo}>
        <div className={styles.userName}>{user.displayName || user.name}</div>
        {user.screenName && (
          <div className={styles.userHandle}>@{user.screenName}</div>
        )}
      </div>
    );
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <span>
            <span className={styles.title}>
              {t("workspace.detail.investigationTeam")}
            </span>
            <span className={styles.memberCount}>
              {members.length + 1}
            </span>
          </span>
          <CloseButton onClick={onClose} size="md" />
        </div>

        {/* Body */}
        <div className={styles.body}>
          {loading ? (
            <div className={styles.emptyState}>
              {t("workspace.team.loading")}
            </div>
          ) : (
            <>
              {/* Search & add */}
              <div className={styles.addSection} ref={searchRef}>
                <div className={styles.searchRow}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    placeholder={t("workspace.team.searchPlaceholder")}
                    disabled={adding}
                  />
                </div>

                {/* Search results dropdown */}
                {searchFocused && searchTerm.trim().length >= 2 && (
                  <div className={styles.searchResults}>
                    {searchResults.length === 0 ? (
                      <div className={styles.searchNoResults}>
                        {t("workspace.team.noResults")}
                      </div>
                    ) : (
                      searchResults.map((user) => (
                        <button
                          key={user.userGuid}
                          type="button"
                          className={styles.searchResultItem}
                          onClick={() => handleAdd(user.userGuid)}
                          disabled={adding}
                        >
                          {renderAvatar(user)}
                          {renderUserInfo(user)}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Team lead */}
              <div>
                <div className={styles.sectionLabel}>
                  {t("workspace.team.leadLabel")}
                </div>
                <div className={styles.leadBanner}>
                  {renderAvatar(leadUser)}
                  {renderUserInfo(leadUser, teamLeadGuid)}
                  <span className={styles.leadBadge}>
                    {t("workspace.team.roleLead")}
                  </span>
                </div>
              </div>

              {/* Member list */}
              {members.length > 0 && (
                <div>
                  <div className={styles.sectionLabel}>
                    {t("workspace.team.membersLabel")}
                  </div>
                  <div className={styles.memberList}>
                    {members.map((member) => {
                      const user = getUser(member.userGuid);
                      return (
                        <div key={member.id} className={styles.memberRow}>
                          {renderAvatar(user)}
                          {renderUserInfo(user, member.userGuid)}
                          <select
                            className={styles.roleSelect}
                            value={member.role}
                            onChange={(e) =>
                              handleRoleChange(
                                member.userGuid,
                                e.target.value
                              )
                            }
                          >
                            {TEAM_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {t(`workspace.team.role_${role}`)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() => handleRemove(member.userGuid)}
                            title={t("workspace.team.remove")}
                            aria-label={t("workspace.team.remove")}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty state when no additional members */}
              {members.length === 0 && (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>👥</span>
                  <span>{t("workspace.team.emptyHint")}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.doneBtn}
            onClick={onClose}
          >
            {t("workspace.team.done")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestigationTeamModal;
